import React, { useState } from 'react';
import { useNavigate } from "react-router-dom";
import { signIn, signOut, fetchUserAttributes, getCurrentUser, resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { showToast } from '../../Constants/Toast';
import { showToastSuccess } from '../../Constants/Toast';
import { useUser } from '../../UserContext';
import { LoginBackground } from "../../Constants/Backgrounds";
import { PasswordTurnVisibleIcon, PasswordTurnInvisibleIcon } from "../../Constants/Icons";
import { orangeButtonCentered } from "../../Constants/Styles";
import VendorNav from "../Homepage/Components/VendorNav";
import { formTextboxLabel } from "../../Constants/Styles";

const VendorLogin = () => {
    const navigate = useNavigate();
    const { setIsSignedIn } = useUser();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSendingReset, setIsSendingReset] = useState(false);
    const [isVerificationStep, setIsVerificationStep] = useState(false);

    const togglePasswordVisibility = () => {
        setIsPasswordVisible(!isPasswordVisible);
    };

    const openForgotPasswordModal = () => {
        setIsModalOpen(true);
    };

    const closeForgotPasswordModal = () => {
        setIsModalOpen(false);
        setIsVerificationStep(false);
        setForgotPasswordEmail('');
        setVerificationCode('');
        setNewPassword('');
    };

    // Step 1: Send Password Reset Email
    async function handleForgotPasswordRequest(event) {
        event.preventDefault();
        setIsSendingReset(true);
        try {
            await resetPassword({
                username: forgotPasswordEmail,
            });
            showToastSuccess('Password reset code sent! Please check your email.');
            setIsVerificationStep(true);  // Move to the next step (enter verification code and new password)
        } catch (error) {
            showToast(`Error: ${error.message}`);
        } finally {
            setIsSendingReset(false);
        }
    }

    // Step 2: Handle Verification Code and New Password Submission
    async function handleForgotPasswordSubmit(event) {
        event.preventDefault();
        setIsSendingReset(true);
        try {
            await confirmResetPassword({
                username: forgotPasswordEmail,
                confirmationCode: verificationCode,
                newPassword: newPassword,
            });
            showToastSuccess('Password successfully reset! You can now sign in with your new password.');
            closeForgotPasswordModal();
        } catch (error) {
            showToast(`Error: ${error.message}`);
        } finally {
            setIsSendingReset(false);
        }
    }

    async function handleLogin(event) {
        event.preventDefault();
        setLoading(true);
        try {
            const { nextStep, signedIn } = await signIn({
                username: email,
                password: password,
            });

            sessionStorage.clear();

            const user = await getCurrentUser();
            const userAttributes = await fetchUserAttributes(['custom:userType']);

            if (userAttributes['custom:userType'] === 'admin') {
                showToast('You are trying to access the vendor portal as an admin!');
                await signOut();
                return;
            }

            setIsSignedIn(true);
            navigate('/vendors/loading');

        } catch (error) {
            if (error.name === 'UserNotFoundException') {
                showToast('Error: User not found. Please check your email and password and try again.');
            } else if (error.name === 'NotAuthorizedException') {
                showToast('Error: Incorrect email or password. Please check your email and password and try again.');
            } else if (error.name === 'UserUnAuthenticatedException') {
                showToast('Error: You have to verify your account first! You will be redirected to the verification page soon.');
                setTimeout(() => {
                    navigate('/vendors/validatePage');
                }, 5000);
            } else {
                showToast('Sorry, there appears to be an issue. Please try again later.');
            }
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="flex flex-col h-screen">
            <VendorNav navbarColor="#26051C" />
            <ToastContainer />
            <div
                className="w-full h-full flex justify-center items-center p-4"
                style={{
                    backgroundImage: `url(${LoginBackground})`,
                    backgroundSize: 'cover',
                    position: 'relative',
                }}
            >
                <div className="absolute inset-0 bg-black opacity-5 z-0" />
                <div className="sm:mt-12 w-full max-w-md h-auto bg-white flex flex-col z-40 shadow-lg p-6 xs:w-[90%]">
                    <div className="px-4 py-4">
                        <h2 className="mt-4 font-proximanova text-[26px] font-[600] text-[#505050] text-center mb-0">Sign in</h2>
                        <p className="font-proximanova text-[18px] text-[#505050] text-center mb-0">to access CharmHealthHub</p>
                        <div className="mt-4 flex flex-col w-full">
                            <label className={formTextboxLabel}>
                                Email
                            </label>
                            <input
                                type="email"
                                className="block py-2 px-4 w-full border border-[#505050] font-proximanova text-[18px] appearance-none focus:outline-none focus:ring-0 focus:border-blue-600"
                                placeholder="Email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>
                        <div className="mt-4 flex flex-col w-full">
                            <label className={formTextboxLabel}>
                                Password
                            </label>
                            <div className="flex flex-row items-center relative">
                                <input
                                    type={isPasswordVisible ? 'text' : 'password'}
                                    className="block py-2 px-4 w-full border border-[#505050] font-proximanova text-[18px] appearance-none focus:outline-none focus:ring-0 focus:border-blue-600"
                                    placeholder="Password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                                <button
                                    type="button"
                                    onClick={togglePasswordVisibility}
                                    className="absolute right-0 mr-4"
                                >
                                    {isPasswordVisible ? (
                                        <PasswordTurnVisibleIcon />
                                    ) : (
                                        <PasswordTurnInvisibleIcon />
                                    )}
                                </button>
                            </div>
                        </div>
                        <div className="w-full mt-3 flex justify-center">
                            <button className="font-proximanova text-charmOrange text-[15px] hover:text-charmHover mb-0" onClick={openForgotPasswordModal}>
                                Forgot Password?
                            </button>
                        </div>
                        <div className="w-full mt-3 flex justify-center items-center">
                            <button
                                className="w-fit h-fit text-white bg-link hover:text-integrationRequestTextHover hover:bg-charmHover rounded-full text-[18px] font-proximanova px-8 py-2 1.5xl:px-10 1.5xl:py-4"
                                onClick={handleLogin}
                                disabled={loading}
                            >
                                {loading && (
                                    <span
                                        className="spinner-border spinner-border-sm mr-2"
                                        role="status"
                                        aria-hidden="true"
                                    ></span>
                                )}
                                Sign In
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Forgot Password Modal */}
            <div
                className={`fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 ${isModalOpen ? '' : 'hidden'}`}
            >
                <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
                    <div className="px-6 pt-4 flex justify-between items-center">
                        <h5 className="text-lg font-semibold mb-0">
                            {isVerificationStep ? 'Reset Password' : 'Forgot Password'}
                        </h5>
                        <button
                            type="button"
                            className="text-gray-400 hover:text-gray-500 focus:outline-none"
                            onClick={closeForgotPasswordModal}
                        >
                            <span className="sr-only">Close</span>
                            &times;
                        </button>
                    </div>
                    <div className="p-6">
                        {!isVerificationStep ? (
                            <form onSubmit={handleForgotPasswordRequest}>
                                <div className="mb-4">
                                    <label className={formTextboxLabel}>
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        className="block py-2 px-4 w-full border border-[#505050] font-proximanova text-[18px] appearance-none focus:outline-none focus:ring-0 focus:border-blue-600"
                                        placeholder="Enter your email"
                                        value={forgotPasswordEmail}
                                        onChange={(e) => setForgotPasswordEmail(e.target.value)}
                                        required
                                    />
                                </div>
                                <button
                                    type="submit"
                                    className={`${orangeButtonCentered} w-full flex items-center justify-center`}
                                    disabled={isSendingReset}
                                >
                                    {isSendingReset ? 'Sending...' : 'Send Reset Link'}
                                </button>
                            </form>
                        ) : (
                            <form onSubmit={handleForgotPasswordSubmit}>
                                <div className="mb-4">
                                    <label className={formTextboxLabel}>
                                        Verification Code
                                    </label>
                                    <input
                                        type="text"
                                        className="block py-2 px-4 w-full border border-[#505050] font-proximanova text-[18px] appearance-none focus:outline-none focus:ring-0 focus:border-blue-600"
                                        placeholder="Enter verification code"
                                        value={verificationCode}
                                        onChange={(e) => setVerificationCode(e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className={formTextboxLabel}>
                                        New Password
                                    </label>
                                    <input
                                        type="password"
                                        className="block py-2 px-4 w-full border border-[#505050] font-proximanova text-[18px] appearance-none focus:outline-none focus:ring-0 focus:border-blue-600"
                                        placeholder="Enter new password"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        required
                                    />
                                </div>
                                <button
                                    type="submit"
                                    className="w-fit h-fit text-white bg-link hover:text-integrationRequestTextHover hover:bg-charmHover rounded-full text-[18px] font-proximanova px-8 py-2 1.5xl:px-10 1.5xl:py-4"
                                    disabled={isSendingReset}
                                >
                                    {isSendingReset ? 'Submitting...' : 'Submit New Password'}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </div>

        </div>
    );
};

export default VendorLogin;
